import { useMutation, useQueryClient } from '@tanstack/react-query';
import { DOOR_ACCESS_PREFIX, doorAccessCountKey } from '../lib/queryKeys';

export interface DoorAccessDelta {
  before: number;
  after: number;
}

export interface UseDoorAccessBatchWrite {
  write: (locationIds: number[], enabled: boolean) => void;
  isWriting: boolean;
  isError: boolean;
  // `null` when the write landed but the counter could not be read on either side of it — there is
  // a jump and the panel does not know its ends. The card says so rather than inventing numbers.
  delta: DoorAccessDelta | null;
  hasWritten: boolean;
}

// The batch write, and the counter jump that is its only receipt: the endpoint answers 200 with an
// empty body, so nothing about WHAT happened comes back over the wire. The panel reads the counter
// on both sides of the write and states the difference.
//
// The delta is the mutation's RETURN VALUE, never a `useState` written from `onSuccess` — that
// would be server-derived data mirrored into local state. The hook holds nothing of its own.
export function useDoorAccessBatchWrite(
  setDoorAccess: (locationIds: number[], enabled: boolean) => Promise<void>,
): UseDoorAccessBatchWrite {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    // A refused destructive bulk write must never be silently re-sent.
    retry: false,
    mutationFn: async ({
      locationIds,
      enabled,
    }: {
      locationIds: number[];
      enabled: boolean;
    }): Promise<DoorAccessDelta | null> => {
      // No second subscription and no second read: the counter card has already put this number in
      // the cache, and taking it before awaiting the write is what makes it the near end.
      const before = queryClient.getQueryData<number>(doorAccessCountKey(true));

      await setDoorAccess(locationIds, enabled);

      // Awaited on purpose, and the reason this diverges from `useClearBoxMarks`'s fire-and-forget:
      // there the caller drives off the refetch state, here the delta IS the result, so the write
      // is not done until it can be stated. The desirable side effect is that `isWriting` covers
      // the refetch, so the buttons cannot go idle before the screen can say what happened.
      await queryClient.invalidateQueries({ queryKey: DOOR_ACCESS_PREFIX });

      const after = queryClient.getQueryData<number>(doorAccessCountKey(true));
      return before !== undefined && after !== undefined ? { before, after } : null;
    },
    // Here and NOT in `onSettled`, which would invalidate a second time on the success path
    // `mutationFn` has already handled. A failed write re-reads the counter anyway, so the screen
    // cannot assert something different from what happened.
    onError: () => {
      void queryClient.invalidateQueries({ queryKey: DOOR_ACCESS_PREFIX });
    },
  });

  return {
    write: (locationIds, enabled) => {
      mutation.mutate({ locationIds, enabled });
    },
    isWriting: mutation.isPending,
    isError: mutation.isError,
    delta: mutation.data ?? null,
    // `isSuccess`, never a comparison of the two ends: a wave whose ids were all already enabled
    // moves the counter by zero, and that still has to announce itself.
    hasWritten: mutation.isSuccess,
  };
}
